/**
 * engineDate.ts — boundary helpers for the engine's date format.
 *
 * The backend stores timestamps as SQLite-native `datetime('now')` output:
 * `'YYYY-MM-DD HH:MM:SS'` in UTC, space-separated, no timezone suffix.
 * That format does NOT parse reliably with `new Date(str)` in Safari (it
 * returns `Invalid Date`). Use these helpers at the API boundary instead.
 *
 * Per `refactor/engine-api-contract-for-graduation.md` §B.2.
 */

/**
 * Parse an engine timestamp string into a Date. Treats the absence of a
 * timezone suffix as UTC (the engine's invariant).
 *
 * Returns `null` for empty/invalid input rather than throwing — most call
 * sites are render code that can fall back to "—".
 */
export function parseEngineDate(input: string | null | undefined): Date | null {
  if (!input) return null;
  const trimmed = String(input).trim();
  if (!trimmed) return null;

  // Already ISO 8601 (`T` separator + timezone or `Z`). Native parsing works.
  if (/T/.test(trimmed) || /Z$/i.test(trimmed) || /[+-]\d{2}:?\d{2}$/.test(trimmed)) {
    const d = new Date(trimmed);
    return isNaN(d.getTime()) ? null : d;
  }

  // SQLite-native: 'YYYY-MM-DD HH:MM:SS' (UTC, no suffix). Promote to ISO
  // by replacing the space with 'T' and appending 'Z'. That's what every
  // browser parses identically.
  const isoish = trimmed.replace(' ', 'T') + 'Z';
  const d = new Date(isoish);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Format a Date as ISO 8601 with milliseconds + 'Z' (UTC). The engine
 * accepts ISO on write (better-sqlite3 stores it as TEXT) so this is the
 * safer write-side format.
 *
 * Returns `null` for null input — call sites pass the result straight
 * into a request body.
 */
export function formatEngineDate(input: Date | null | undefined): string | null {
  if (!input) return null;
  return input.toISOString();
}

/**
 * Convenience: parse an engine date and return an ISO 8601 string suitable
 * for shapes that expect ISO (e.g. UnifiedCardData.created_at). Returns
 * an empty string for invalid input — matches existing UnifiedCard render
 * behavior where missing dates show as "—".
 */
export function engineDateToIso(input: string | null | undefined): string {
  const d = parseEngineDate(input);
  return d ? d.toISOString() : '';
}
